import {isUndefined} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {
  IntersperseItem,
  intersperseNodes,
} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  CalendarsPanelId,
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {maybeBuildRecurrenceText} from '../../../../../helpers/models/calendarEventHelpers';
import {CalendarEventModel} from '../../../../../models/api/calendarEventModel';
import {CalendarEventVersionModel} from '../../../../../models/api/calendarEventVersionModel';
import {VisualParticipantModel} from '../../../../../models/api/visualParticipantModel';
import {CalendarEventLocationRenderer} from '../../../../common/calendarEvent/calendarEventLocationRenderer';
import {CalendarInviteViewerParticipants} from './calendarInviteViewerParticipants';

/*
 * Props.
 */

export interface CalendarInviteViewerDetailsProps {
  panelId: CalendarsPanelId | ViewerPanelId | MessagePanelId | EmlPanelId;
  calendarEvent: CalendarEventModel;
  calendarEventVersion: CalendarEventVersionModel;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
}

/*
 * Style.
 */

const StyledDetailsDiv = styled.div`
  display: grid;
  grid-column-gap: 8px;
  grid-row-gap: 10px;
  grid-template-columns: auto 1fr;
  padding: 14px 16px;
`;

const StyledIconDiv = styled.div`
  padding: 3px 0;
`;

const StyledContentDiv = styled.div`
  line-height: 22px;
`;

const StyledLocationSpan = styled.span`
  user-select: text;
  p {
    display: inline;
    margin: 0px;
  }
  a {
    cursor: pointer;
    color: ${(p) => p.theme.interactiveText};
  }
`;

/*
 * Component.
 */

export const CalendarInviteViewerDetails: FC<CalendarInviteViewerDetailsProps> = (props) => {
  const theme = useUserTheme();

  return (
    <StyledDetailsDiv>
      {maybeRenderRecurrenceSection(props.calendarEvent)}
      {maybeRenderLocationSection(props.calendarEventVersion)}
      <StyledIconDiv>
        <Icon name="teammatesSmall" color={theme.greys.shade70} />
      </StyledIconDiv>
      <CalendarInviteViewerParticipants
        panelId={props.panelId}
        visualParticipants={props.visualParticipants}
      />
    </StyledDetailsDiv>
  );
};

/*
 * Recurrence section.
 */

function maybeRenderRecurrenceSection(calendarEvent: CalendarEventModel) {
  const recurrenceText = maybeBuildRecurrenceText(calendarEvent.recurrence);
  if (isUndefined(recurrenceText)) {
    return null;
  }

  return (
    <>
      <UserThemeConsumer>
        {(theme) => (
          <StyledIconDiv>
            <Icon name="repeatSmall" color={theme.greys.shade70} />
          </StyledIconDiv>
        )}
      </UserThemeConsumer>
      <StyledContentDiv>{recurrenceText}</StyledContentDiv>
    </>
  );
}

/*
 * Location section.
 */

function maybeRenderLocationSection(calendarEventVersion: CalendarEventVersionModel) {
  // Don't show this section if there are no locations for this event.
  const {locations} = calendarEventVersion;
  if (!locations.length) {
    return null;
  }

  const locationsRender: ReadonlyArray<IntersperseItem> = calendarEventVersion.locations.map((l, index) => ({
    key: index,
    node: (
      <StyledLocationSpan>
        <CalendarEventLocationRenderer location={l} />
      </StyledLocationSpan>
    ),
  }));

  // Display a comma separated list of location(s).
  // If a location has an address, display a Google Maps link for it.
  return (
    <>
      <UserThemeConsumer>
        {(theme) => (
          <StyledIconDiv>
            <Icon name="locationSmall" color={theme.greys.shade70} />
          </StyledIconDiv>
        )}
      </UserThemeConsumer>
      <StyledContentDiv>{intersperseNodes(locationsRender, () => ',')}</StyledContentDiv>
    </>
  );
}
